import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { tooltipSurfaceClassName } from '../components/data-display/tooltip';
import { cn } from '../utils';

/** Gap between the cell and the panel, matching `TooltipContent`'s offset. */
const OFFSET_PX = 8;
/** Keeps the panel clear of the viewport edges, as `FilterPopover` does. */
const MARGIN_PX = 8;
/** Sub-pixel text layout otherwise reports a cell as overflowing by 0.3px. */
const OVERFLOW_SLACK_PX = 1;
/**
 * A class rather than an inline width so the hidden measuring pass and the
 * positioned one agree: measuring an unconstrained panel and *then* capping it
 * would place a two-line tooltip using a one-line height.
 */
const PANEL_WIDTH_CLASS = 'max-w-[min(480px,calc(100vw-16px))]';
/**
 * Elements to fall back to when the cell itself is not the thing being clipped.
 * `truncate` is Tailwind's, and is how every cell in this repo that clips its
 * own contents says so — see `clippedElement`.
 */
const INNER_SELECTOR = '.truncate';

const HIDDEN: CSSProperties = { position: 'fixed', visibility: 'hidden' };

interface Anchor {
  text: string;
  top: number;
  bottom: number;
  left: number;
}

/**
 * Reveals a table cell's full text when the cell is too narrow to show it.
 *
 * Clipping itself is pure CSS (`[data-dt-truncate]` in `globals.css`); "is this
 * particular cell clipped" is not — it is a `scrollWidth > clientWidth`
 * measurement that can only happen at runtime. That does not mean a tooltip
 * component per cell: a virtualized 60-row table would mount several hundred of
 * them, and the ~30 hand-written tables in the app would each need every `<td>`
 * edited. Instead one delegated listener and one floating panel per table, so a
 * table adopts the behaviour by calling this hook and tagging its container —
 * with no changes to its cells at all.
 *
 * Header cells count as cells: a `<th>` is clipped and revealed on the same
 * terms as the `<td>`s under it, which matters most on the columns narrow
 * enough that the reader cannot tell what they are looking at.
 *
 * Opt a cell out with `data-truncate="off"`, which also switches off the CSS
 * clipping. The tooltip's text is read straight off the clipped element, so
 * there is no accessor to keep in sync.
 *
 * Hover and focus only: rows are not focusable today, so this is not a keyboard
 * path and should not be described as an accessibility feature. The `focusin`
 * half costs nothing and starts working the day rows gain focus.
 */
export function useTruncationTooltip(
  containerRef: RefObject<HTMLElement | null>,
): ReactNode {
  // Memoised on the ref so the parent table re-rendering never re-creates the
  // element: showing a tooltip then re-renders the panel alone, not every row.
  return useMemo(
    () => <TruncationTooltip containerRef={containerRef} />,
    [containerRef],
  );
}

function TruncationTooltip({
  containerRef,
}: {
  containerRef: RefObject<HTMLElement | null>;
}) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [style, setStyle] = useState<CSSProperties>(HIDDEN);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let activeCell: HTMLTableCellElement | null = null;

    function hide() {
      activeCell = null;
      setAnchor(null);
    }

    function show(event: Event) {
      const target = event.target;
      const cell =
        target instanceof Element
          ? target.closest<HTMLTableCellElement>('td, th')
          : null;
      if (!cell || cell === activeCell) {
        if (!cell) hide();
        return;
      }
      const clipped = clippedElement(cell);
      if (!clipped) {
        hide();
        return;
      }
      const text = clipped.innerText.trim();
      if (!text) {
        hide();
        return;
      }
      const rect = clipped.getBoundingClientRect();
      activeCell = cell;
      setAnchor({ text, top: rect.top, bottom: rect.bottom, left: rect.left });
      setStyle(HIDDEN);
    }

    container.addEventListener('pointerover', show);
    container.addEventListener('focusin', show);
    container.addEventListener('pointerleave', hide);
    container.addEventListener('focusout', hide);
    container.addEventListener('pointerdown', hide);
    // Capture, because the scroll happens on a pane inside the container and
    // scroll events do not bubble.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      container.removeEventListener('pointerover', show);
      container.removeEventListener('focusin', show);
      container.removeEventListener('pointerleave', hide);
      container.removeEventListener('focusout', hide);
      container.removeEventListener('pointerdown', hide);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [containerRef]);

  useLayoutEffect(() => {
    if (!anchor || !panelRef.current) return;
    const panel = panelRef.current.getBoundingClientRect();
    const left = Math.min(
      Math.max(MARGIN_PX, anchor.left),
      Math.max(MARGIN_PX, window.innerWidth - panel.width - MARGIN_PX),
    );
    const below = anchor.bottom + OFFSET_PX;
    const fitsBelow = below + panel.height <= window.innerHeight - MARGIN_PX;
    setStyle({
      position: 'fixed',
      top: fitsBelow ? below : anchor.top - OFFSET_PX - panel.height,
      left,
      zIndex: 80,
    });
  }, [anchor]);

  if (!anchor || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={panelRef}
      role="tooltip"
      style={style}
      className={cn(
        tooltipSurfaceClassName,
        PANEL_WIDTH_CLASS,
        'animate-fade-in break-words',
      )}
    >
      {anchor.text}
    </div>,
    document.body,
  );
}

/**
 * The element inside `cell` whose text is actually being cut off, if any.
 *
 * Usually the cell itself. Not always: a header cell lays its label out next to
 * a sort icon and a filter button, so the label carries `truncate` and clips
 * one level in — leaving the `<th>` measuring as though everything fits. Same
 * shape as a `<td>` holding a badge beside a name. Probing the cell alone would
 * silently skip both.
 */
function clippedElement(cell: HTMLTableCellElement): HTMLElement | null {
  if (cell.dataset.truncate === 'off') return null;
  if (isClipped(cell)) return cell;
  for (const child of cell.querySelectorAll<HTMLElement>(INNER_SELECTOR)) {
    if (isClipped(child)) return child;
  }
  return null;
}

function isClipped(element: HTMLElement) {
  return element.scrollWidth > element.clientWidth + OVERFLOW_SLACK_PX;
}
